/**
 * States / provinces / territories for US and AU for dropdowns.
 * Used when academy or user country is US or Australia.
 */

export interface StateOption {
  code: string;
  name: string;
}

/** US states and DC (2-letter codes) */
export const US_STATES: StateOption[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

/** Australian states and territories */
export const AU_STATES: StateOption[] = [
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'WA', name: 'Western Australia' },
];

export const STATES_BY_COUNTRY: Record<string, StateOption[]> = {
  US: US_STATES,
  AU: AU_STATES,
};

export function getStatesForCountry(countryCode: string): StateOption[] {
  const code = countryCode?.trim().toUpperCase();
  return STATES_BY_COUNTRY[code] ?? [];
}

export function isStateRequiredForCountry(countryCode: string): boolean {
  return countryCode?.trim().toUpperCase() === 'US' || countryCode?.trim().toUpperCase() === 'AU';
}

/** Normalize a state/province label to the code Stripe Tax expects (e.g. "New South Wales" → "NSW"). */
export function resolveStripeTaxStateCode(
  countryCode: string,
  stateOrProvince?: string | null
): string | undefined {
  const trimmed = stateOrProvince?.trim();
  if (!trimmed) return undefined;

  const upper = trimmed.toUpperCase();
  const states = getStatesForCountry(countryCode);
  const byCode = states.find((state) => state.code.toUpperCase() === upper);
  if (byCode) return byCode.code;

  const byName = states.find((state) => state.name.toLowerCase() === trimmed.toLowerCase());
  return byName?.code ?? trimmed;
}
